import { useState, useEffect } from "react";
import { usePresentationsStore, PresentationData } from "@/data/presentationsStore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FileText, Save, Image as ImageIcon, Eye, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface PartnerPresentation {
  entityId: string;
  role: string;
  userName: string;
  data: PresentationData;
}

interface PartnerPresentation {
  userId: string;
  userName: string; // We'll try to resolve this if possible, or fallback to userId
  role: string;
  data: PresentationData;
}

export default function AdminPresentationsPage() {
  const [presentations, setPresentations] = useState<PartnerPresentation[]>([]);
  const [selectedPres, setSelectedPres] = useState<PartnerPresentation | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [formData, setFormData] = useState<PresentationData | null>(null);
  const [activeTab, setActiveTab] = useState<string>("ALL");

  const { presentations: storePresentations, fetchPresentations, savePresentation } = usePresentationsStore();

  const loadPresentations = async () => {
    await fetchPresentations();
  };

  useEffect(() => {
    loadPresentations();
  }, [fetchPresentations]);

  useEffect(() => {
    const presList: PartnerPresentation[] = storePresentations.map(p => ({
      entityId: p.entityId,
      role: p.role,
      userName: p.entityId, // EntityId is typically the acronym or name
      data: p.data || {} as PresentationData
    }));
    setPresentations(presList);
  }, [storePresentations]);

  const handleEdit = (pres: PartnerPresentation) => {
    setSelectedPres(pres);
    setFormData({ ...pres.data });
    setIsEditOpen(true);
  };

  const handleDelete = (entityId: string, role: string) => {
    if (confirm("Voulez-vous vraiment vider cette présentation ? (Elle sera réinitialisée)")) {
      savePresentation(entityId, role, {} as PresentationData).then(() => {
        toast.success("Présentation réinitialisée");
        loadPresentations();
      });
    }
  };

  const handleChange = (field: string, value: string) => {
    if (formData) {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, field: string) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("L'image est trop grande (max 2MB)");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        if (formData) {
          setFormData({ ...formData, [field]: reader.result as string });
          toast.success("Image chargée localement !");
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (selectedPres && formData) {
      const success = await savePresentation(selectedPres.entityId, selectedPres.role, formData);
      if (success) {
        toast.success("Présentation mise à jour avec succès");
        setIsEditOpen(false);
        loadPresentations();
      }
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div>
        <h1 className="text-2xl md:text-3xl font-display font-bold text-foreground">Présentations Partenaires</h1>
        <p className="text-muted-foreground mt-2">Gérez les fiches de présentation des partis, ONG, institutions et médias.</p>
      </div>

      <Tabs defaultValue="ALL" onValueChange={setActiveTab}>
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="ALL">Toutes</TabsTrigger>
          <TabsTrigger value="PARTY">Partis Politiques</TabsTrigger>
          <TabsTrigger value="ONG">ONG / Associations</TabsTrigger>
          <TabsTrigger value="INSTITUTION">Institutions</TabsTrigger>
          <TabsTrigger value="MEDIA">Médias</TabsTrigger>
        </TabsList>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {presentations.filter(p => activeTab === "ALL" || p.role === activeTab).length === 0 ? (
          <div className="col-span-full py-12 text-center text-muted-foreground bg-card rounded-xl border border-border border-dashed">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p>Aucune présentation partenaire n'a été trouvée.</p>
          </div>
        ) : (
          presentations.filter(p => activeTab === "ALL" || p.role === activeTab).map((pres) => (
            <Card key={`${pres.entityId}-${pres.role}`} className="shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
              <div className="h-2 w-full bg-primary" />
              <CardHeader className="pb-3 flex flex-row items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center shrink-0 overflow-hidden border border-border/50">
                    {pres.data?.logoUrl ? (
                      <img src={pres.data.logoUrl} alt="Logo" className="w-full h-full object-contain p-1 bg-white" />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <CardTitle className="text-lg font-display truncate max-w-[150px]">{pres.userName}</CardTitle>
                    <p className="text-xs text-muted-foreground font-semibold uppercase">{pres.role}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <p className="text-sm text-muted-foreground line-clamp-3 mb-4 flex-1">
                  {pres.data?.description || <span className="italic">Aucune description fournie</span>}
                </p>
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-border/50">
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(pres)} className="h-8 gap-1.5 text-xs">
                      <Edit className="w-3 h-3" /> Éditer
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(pres.entityId, pres.role)} className="h-8 w-8 p-0 text-destructive border-destructive/20 hover:bg-destructive/10">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  {pres.data?.website && (
                    <a href={pres.data.website} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline font-medium">
                      Site web ↗
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      </Tabs>

      {/* Modal d'édition */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-[700px] p-0 rounded-2xl overflow-hidden border-none shadow-2xl h-[90vh] flex flex-col">
          <div className="h-2 w-full bg-gradient-to-r from-primary to-accent shrink-0" />
          <div className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle className="text-2xl font-display text-primary flex items-center gap-2">
              <Edit className="w-6 h-6" />
              Modifier la Présentation
            </DialogTitle>
            <DialogDescription className="mt-2 text-muted-foreground font-body">
              Modification de la fiche pour {selectedPres?.userName}.
            </DialogDescription>
          </div>

          <ScrollArea className="flex-1 px-6 py-4">
            {formData && (
              <div className="space-y-6 pb-6">
                <div className="bg-muted/30 p-5 rounded-2xl space-y-5 border border-border/50">
                  <h3 className="font-display font-semibold text-foreground flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-primary" />
                    Identité Visuelle
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2 space-y-2">
                      <Label className="text-sm font-medium text-foreground">Lien du Logo (URL)</Label>
                      <div className="flex gap-2">
                        <Input type="url" placeholder="URL ou upload..." value={formData.logoUrl || ""} onChange={(e) => handleChange('logoUrl', e.target.value)} className="w-full bg-background" />
                        <Label className="cursor-pointer bg-primary/10 text-primary hover:bg-primary/20 px-4 rounded-md flex items-center justify-center font-semibold text-sm transition-colors whitespace-nowrap">
                          Uploader
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, 'logoUrl')} />
                        </Label>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-foreground">Image Représentative 1</Label>
                      <div className="flex gap-2">
                        <Input type="url" placeholder="URL ou upload..." value={formData.image1Url || ""} onChange={(e) => handleChange('image1Url', e.target.value)} className="w-full bg-background" />
                        <Label className="cursor-pointer bg-primary/10 text-primary hover:bg-primary/20 px-3 rounded-md flex items-center justify-center font-semibold text-xs transition-colors">
                          Upload
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, 'image1Url')} />
                        </Label>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-foreground">Image Représentative 2</Label>
                      <div className="flex gap-2">
                        <Input type="url" placeholder="URL ou upload..." value={formData.image2Url || ""} onChange={(e) => handleChange('image2Url', e.target.value)} className="w-full bg-background" />
                        <Label className="cursor-pointer bg-primary/10 text-primary hover:bg-primary/20 px-3 rounded-md flex items-center justify-center font-semibold text-xs transition-colors">
                          Upload
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, 'image2Url')} />
                        </Label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-muted/30 p-5 rounded-2xl space-y-5 border border-border/50">
                  <h3 className="font-display font-semibold text-foreground flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Informations Détaillées
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-foreground">Description générale</Label>
                      <Textarea placeholder="Présentation globale..." value={formData.description || ""} onChange={(e) => handleChange('description', e.target.value)} className="min-h-[100px] bg-background" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-foreground">Activités et Services</Label>
                      <Textarea placeholder="Quelles sont vos activités ?" value={formData.activites || ""} onChange={(e) => handleChange('activites', e.target.value)} className="min-h-[80px] bg-background" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-foreground">Projets Actuels et Futurs</Label>
                      <Textarea placeholder="Projets clés..." value={formData.projets || ""} onChange={(e) => handleChange('projets', e.target.value)} className="min-h-[80px] bg-background" />
                    </div>
                  </div>
                </div>

                <div className="bg-muted/30 p-5 rounded-2xl space-y-5 border border-border/50">
                  <h3 className="font-display font-semibold text-foreground flex items-center gap-2">
                    <XIcon className="w-5 h-5 text-primary" />
                    Liens et Réseaux
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2 space-y-2">
                      <Label className="text-sm font-medium text-foreground">Site Web</Label>
                      <Input type="url" placeholder="https://..." value={formData.website || ""} onChange={(e) => handleChange('website', e.target.value)} className="bg-background" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-foreground">Facebook</Label>
                      <Input type="url" placeholder="https://facebook.com/..." value={formData.facebook || ""} onChange={(e) => handleChange('facebook', e.target.value)} className="bg-background" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-foreground">X</Label>
                      <Input type="url" placeholder="https://x.com/..." value={formData.twitter || ""} onChange={(e) => handleChange('twitter', e.target.value)} className="bg-background" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-foreground">Instagram</Label>
                      <Input type="url" placeholder="https://instagram.com/..." value={formData.instagram || ""} onChange={(e) => handleChange('instagram', e.target.value)} className="bg-background" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-foreground">YouTube</Label>
                      <Input type="url" placeholder="https://youtube.com/..." value={formData.youtube || ""} onChange={(e) => handleChange('youtube', e.target.value)} className="bg-background" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </ScrollArea>
          
          <div className="p-6 bg-background border-t border-border shrink-0 mt-auto">
            <div className="flex gap-4">
              <Button variant="outline" onClick={() => setIsEditOpen(false)} className="flex-1 font-body h-12">
                Annuler
              </Button>
              <Button onClick={handleSave} className="flex-1 gap-2 font-body font-semibold h-12 bg-primary hover:bg-primary/90 text-white shadow-md">
                <Save className="w-5 h-5" /> Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
